import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, AlertCircle, CheckCircle2, Lightbulb, ArrowRight, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { WeeklyInsight } from '../../types/insight';
interface WeeklyInsightCardProps {
  insight: WeeklyInsight;
  onDismiss?: () => void;
}
export function WeeklyInsightCard({
  insight,
  onDismiss
}: WeeklyInsightCardProps) {
  const getIcon = () => {
    switch (insight.type) {
      case 'improvement':
        return <TrendingDown className="w-6 h-6 text-green-500" data-id="element-1024" data-mp="3tb3iaahv0t1g" />;
      // Down is good for toxicity
      case 'regression':
        return <AlertCircle className="w-6 h-6 text-[#FF0000]" data-id="element-1025" data-mp="3ogz6ndxnfwu1" />;
      case 'pattern_emerged':
        return <Lightbulb className="w-6 h-6 text-orange-500" data-id="element-1026" data-mp="3jhwe2syuvj2y" />;
      case 'milestone':
        return <CheckCircle2 className="w-6 h-6 text-blue-500" data-id="element-1027" data-mp="kv9z1nw593gp" />;
      default:
        return <Sparkles className="w-6 h-6 text-[#FF0000]" data-id="element-1028" data-mp="g2gup14l7x55" />;
    }
  };
  const getColors = () => {
    switch (insight.type) {
      case 'improvement':
        return 'bg-green-500';
      case 'regression':
        return 'bg-[#FF0000]';
      case 'pattern_emerged':
        return 'bg-orange-500';
      case 'milestone':
        return 'bg-blue-500';
      default:
        return 'bg-[#FF0000]';
    }
  };
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="relative bg-[#0a0a0a] border border-gray-800 p-6" data-id="element-1029" data-mp="1cbwjuiqzcvi">
      {/* Accent Bar */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${getColors()}`} data-id="element-1030" data-mp="3nis3le8or7xk" />

      {/* Corner Brackets */}
      <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600" data-id="element-1031" data-mp="3ekf1fkyjpgcq" />
      <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600" data-id="element-1032" data-mp="3em9k0smcgwdn" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600" data-id="element-1033" data-mp="17rw4z1enlr8q" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600" data-id="element-1034" data-mp="1cqz82gb2pne2" />

      <div className="flex items-start gap-4" data-id="element-1035" data-mp="1bd7mtl5x2rr8">
        <div className="p-2.5 bg-gray-800 border border-gray-700" data-id="element-1036" data-mp="16ca497gzvvp7">
          {getIcon()}
        </div>

        <div className="flex-1" data-id="element-1037" data-mp="11cx79twet88w">
          <div className="flex items-center justify-between mb-2" data-id="element-1038" data-mp="11e8l6mjkjwdq">
            <h3 className="font-bold text-white text-lg tracking-tight uppercase" data-id="element-1039" data-mp="wff9mznuefg7">
              {insight.title}
            </h3>
            {insight.priority === 'high' && <span className="px-2 py-0.5 border border-gray-700 bg-gray-800 text-gray-400 text-[10px] font-mono uppercase tracking-widest" data-id="element-1040" data-mp="1p3ktzlazi2fi">
                Weekly Intel
              </span>}
          </div>

          <p className="text-gray-400 font-mono text-sm leading-relaxed mb-4" data-id="element-1041" data-mp="1fe9lo0vx91so">
            {insight.description}
          </p>

          {insight.metric && <div className="flex items-center gap-4 mb-4" data-id="element-1042" data-mp="2cuqbe4wkp2rx">
              <div className="flex flex-col" data-id="element-1043" data-mp="281dnxy3mr20r">
                <span className="text-[10px] font-mono text-gray-500 uppercase tracking-widest" data-id="element-1044" data-mp="2gat55zw2txxu">
                  {insight.metric.label}
                </span>
                <span className="text-2xl font-black text-white font-mono" data-id="element-1045" data-mp="2kcwple0q4cv9">
                  {insight.metric.value}
                  {insight.metric.unit === '%' && '%'}
                </span>
              </div>

              {insight.metric.change !== 0 && <div className={`flex items-center px-2 py-1 border text-[10px] font-mono uppercase tracking-widest ${insight.metric.direction === 'down' && insight.type === 'improvement' || insight.metric.direction === 'up' && insight.type !== 'regression' ? 'bg-green-500/10 text-green-500 border-green-500/30' : 'bg-[#FF0000]/10 text-[#FF0000] border-[#FF0000]/30'}`} data-id="element-1046" data-mp="2zagrgc2bprx7">
                  {insight.metric.direction === 'up' ? <TrendingUp size={12} className="mr-1" data-id="element-1047" data-mp="3mz9ipv803blh" /> : <TrendingDown size={12} className="mr-1" data-id="element-1048" data-mp="3mwvmhj1l3tcc" />}
                  {Math.abs(insight.metric.change)}%
                </div>}
            </div>}

          {insight.actionLink && <Link to={insight.actionLink} className="inline-flex items-center text-sm font-mono text-[#FF0000] hover:text-[#FF0000]/80 uppercase tracking-wider transition-colors group" data-id="element-1049" data-mp="xbbpem8b0nli">
              {insight.actionText || 'View Details'}
              <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" data-id="element-1050" data-mp="3vsxqwnrwpr37" />
            </Link>}
        </div>
      </div>
    </motion.div>;
}